// Ask Your Data reducer.
//
// State shape:
//   {
//     sessions:        [{session_id, title, updated_at, turn_count, total_credits_spent}],
//     activeSessionId: string | null,
//     messages:        [{role, content, envelope, sources, usage, created_at, pending}],
//     isLoading:       bool,        // a turn or session-load in flight
//     isLoadingSessions: bool,
//     error:           {kind, message, balance?} | null,
//     lastUsage:       {input_tokens, output_tokens, cost_credits, ...} | null,
//     totalCredits:    number,      // running total for the active session
//   }

import {
  ASK_REQUEST, ASK_SUCCESS, ASK_FAILURE,
  SESSIONS_REQUEST, SESSIONS_SUCCESS, SESSIONS_FAILURE,
  SESSION_LOAD_REQUEST, SESSION_LOAD_SUCCESS, SESSION_LOAD_FAILURE,
  SESSION_DELETE_REQUEST, SESSION_DELETE_SUCCESS, SESSION_DELETE_FAILURE,
  NEW_SESSION, SELECT_SESSION, APPEND_PENDING, CLEAR_ERROR,

  STREAM_REQUEST, STREAM_START, STREAM_TEXT_DELTA,
  STREAM_TOOL_START, STREAM_TOOL_END, STREAM_ATTACHMENT,
  STREAM_DONE, STREAM_ERROR,
} from '../../actions/signals/askData';

const initialState = {
  sessions:          [],
  activeSessionId:   null,
  messages:          [],
  isLoading:         false,
  isLoadingSessions: false,
  // Flips true after the first successful sessions fetch - gates the
  // setup kickoff so it never fires on stale/unknown state.
  sessionsLoaded:    false,
  isDeleting:        false,
  error:             null,
  lastUsage:         null,
  totalCredits:      0,
  // The latest LIVE milestone artifact (a goal reaching serving via a
  // chat tool call). Set only from streaming - never on session reload -
  // so the dock's auto-handoff can't re-fire on old transcripts.
  lastMilestone:     null,   // {spec, ts}
  lastPageTour:      null,   // {spec, ts} - show_page walked the user somewhere
};


// Convert a stored turn into the live message shape. Prefers the
// new `blocks` array (chronological text/tool/attachment trail
// written by the streaming orchestrator) and falls back to a
// best-effort synthesis from envelope+sources for legacy turns
// that predate the block format.
function turnToMessage(t) {
  const base = {
    role:       t.role,
    content:    t.content || '',
    envelope:   t.envelope || null,
    sources:    t.sources || [],
    usage:      t.usage || null,
    created_at: t.created_at || null,
    pending:    false,
    streaming:  false,
    blocks:     [],
    // File chips on user bubbles ({filename, size_bytes, ...}).
    attachments: Array.isArray(t.attachments) ? t.attachments : [],
    // App-initiated turns (setup kickoff) - context, never rendered.
    hidden:     Boolean(t.hidden),
  };
  if (t.role !== 'assistant') return base;
  if (Array.isArray(t.blocks) && t.blocks.length) {
    base.blocks = t.blocks;
    return base;
  }
  // Legacy synthesis: text first, then tool steps, then any
  // attachments. Order is approximate for these older turns.
  const blocks = [];
  const env = t.envelope || {};
  if (env.text) blocks.push({ type: 'text', text: env.text });
  for (const s of (t.sources || [])) {
    blocks.push({
      type:         'tool',
      tool_call_id: null,
      name:         s.tool || 'run_sql',
      label:        null,
      status:       s.ok ? 'success' : 'error',
      spec:         s.spec || {},
      row_count:    s.row_count,
      error:        s.error,
    });
  }
  if (env.chart) blocks.push({ type: 'attachment', kind: 'chart', spec: env.chart });
  if (env.table) blocks.push({ type: 'attachment', kind: 'table', spec: env.table });
  base.blocks = blocks;
  return base;
}


function transcriptToMessages(turns) {
  if (!Array.isArray(turns)) return [];
  return turns.map(turnToMessage);
}


export const reducer = (state = initialState, action) => {
  switch (action.type) {
    // -----------------------------------------------------------------
    // Local UI ops
    // -----------------------------------------------------------------
    case NEW_SESSION:
      return {
        ...state,
        activeSessionId: null,
        messages:        [],
        lastUsage:       null,
        totalCredits:    0,
        error:           null,
      };

    case SELECT_SESSION: {
      const sid = action.payload?.session_id || null;
      const session = state.sessions.find((s) => s.session_id === sid);
      return {
        ...state,
        activeSessionId: sid,
        totalCredits:    session?.total_credits_spent || 0,
        error:           null,
      };
    }

    case CLEAR_ERROR:
      return { ...state, error: null };

    case APPEND_PENDING: {
      const q = action.payload?.question || '';
      const attachments = Array.isArray(action.payload?.attachments)
        ? action.payload.attachments
        : [];
      const hidden = Boolean(action.payload?.hidden);
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            role: 'user', content: q, attachments, hidden,
            pending: false, streaming: false,
            created_at: new Date().toISOString(),
          },
          {
            role: 'assistant', content: '',
            pending: true, streaming: true,
            envelope: null, sources: [], usage: null,
            blocks: [],   // chronological text/tool/attachment trail
            created_at: null,
          },
        ],
      };
    }

    // -----------------------------------------------------------------
    // Ask turn
    // -----------------------------------------------------------------
    case ASK_REQUEST:
      return { ...state, isLoading: true, error: null };

    case ASK_SUCCESS: {
      const p = action.payload || {};
      // Drop the trailing pending placeholder if there is one.
      const trimmed = state.messages.length && state.messages[state.messages.length - 1]?.pending
        ? state.messages.slice(0, -1)
        : state.messages;
      const assistantMsg = {
        role:       'assistant',
        content:    p.answer?.text || '',
        envelope:   p.answer || null,
        sources:    p.sources || [],
        usage:      p.usage || null,
        created_at: new Date().toISOString(),
        pending:    false,
      };
      // Update the sessions list metadata so the sidebar reflects the
      // newly-created session immediately (no extra round-trip).
      let sessions = state.sessions;
      if (p.session_id) {
        const existing = sessions.find((s) => s.session_id === p.session_id);
        if (existing) {
          sessions = sessions.map((s) =>
            s.session_id === p.session_id
              ? {
                  ...s,
                  title: p.title || s.title,
                  updated_at: assistantMsg.created_at,
                  turn_count: (s.turn_count || 0) + 2,
                  total_credits_spent: (s.total_credits_spent || 0) + (p.usage?.cost_credits || 0),
                }
              : s
          );
        } else {
          sessions = [
            {
              session_id: p.session_id,
              title:      p.title || 'New conversation',
              created_at: assistantMsg.created_at,
              updated_at: assistantMsg.created_at,
              turn_count: 2,
              total_credits_spent: p.usage?.cost_credits || 0,
            },
            ...sessions,
          ];
        }
      }
      return {
        ...state,
        isLoading:       false,
        activeSessionId: p.session_id || state.activeSessionId,
        messages:        [...trimmed, assistantMsg],
        lastUsage:       p.usage || null,
        totalCredits:    state.totalCredits + (p.usage?.cost_credits || 0),
        sessions,
        error:           null,
      };
    }

    case ASK_FAILURE: {
      // Remove the pending placeholder.
      const trimmed = state.messages.length && state.messages[state.messages.length - 1]?.pending
        ? state.messages.slice(0, -1)
        : state.messages;
      return {
        ...state,
        isLoading: false,
        messages:  trimmed,
        error:     action.payload || { kind: 'error', message: 'Failed' },
      };
    }

    // -----------------------------------------------------------------
    // Sessions list
    // -----------------------------------------------------------------
    case SESSIONS_REQUEST:
      return { ...state, isLoadingSessions: true };

    case SESSIONS_SUCCESS:
      return {
        ...state,
        isLoadingSessions: false,
        sessionsLoaded: true,
        sessions: Array.isArray(action.payload) ? action.payload : [],
      };

    case SESSIONS_FAILURE:
      return { ...state, isLoadingSessions: false };

    // -----------------------------------------------------------------
    // Single-session transcript
    // -----------------------------------------------------------------
    case SESSION_LOAD_REQUEST:
      return { ...state, isLoading: true, error: null };

    case SESSION_LOAD_SUCCESS: {
      const session = action.payload || null;
      if (!session) {
        return { ...state, isLoading: false };
      }
      return {
        ...state,
        isLoading:       false,
        activeSessionId: session.session_id,
        messages:        transcriptToMessages(session.turns),
        totalCredits:    session.total_credits_spent || 0,
      };
    }

    case SESSION_LOAD_FAILURE:
      return { ...state, isLoading: false };

    // -----------------------------------------------------------------
    // Delete session
    // -----------------------------------------------------------------
    case SESSION_DELETE_REQUEST:
      return { ...state, isDeleting: true };

    case SESSION_DELETE_SUCCESS: {
      const sid = action.payload?.session_id;
      const remaining = state.sessions.filter((s) => s.session_id !== sid);
      const isActive = state.activeSessionId === sid;
      return {
        ...state,
        isDeleting:      false,
        sessions:        remaining,
        activeSessionId: isActive ? null : state.activeSessionId,
        messages:        isActive ? [] : state.messages,
        totalCredits:    isActive ? 0 : state.totalCredits,
      };
    }

    case SESSION_DELETE_FAILURE:
      return { ...state, isDeleting: false };

    // -----------------------------------------------------------------
    // Streaming (SSE)
    // -----------------------------------------------------------------
    case STREAM_REQUEST:
      return { ...state, isLoading: true, error: null };

    case STREAM_START: {
      const p = action.payload || {};
      // The placeholder assistant message is already in `messages`
      // (added by APPEND_PENDING). Only update activeSessionId/title.
      let sessions = state.sessions;
      if (p.session_id) {
        const exists = sessions.some((s) => s.session_id === p.session_id);
        if (!exists) {
          sessions = [
            {
              session_id: p.session_id,
              title:      p.title || 'New conversation',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              turn_count: 0,
              total_credits_spent: 0,
            },
            ...sessions,
          ];
        }
      }
      return {
        ...state,
        activeSessionId: p.session_id || state.activeSessionId,
        sessions,
      };
    }

    case STREAM_TEXT_DELTA: {
      const text = action.payload?.text || '';
      if (!text) return state;
      const messages = updateLastAssistant(state.messages, (m) => {
        const blocks = appendTextDelta(m.blocks || [], text);
        return {
          ...m,
          content: (m.content || '') + text,
          blocks,
        };
      });
      return { ...state, messages };
    }

    case STREAM_TOOL_START: {
      const p = action.payload || {};
      const messages = updateLastAssistant(state.messages, (m) => ({
        ...m,
        blocks: [
          ...(m.blocks || []),
          {
            type:         'tool',
            tool_call_id: p.tool_call_id,
            name:         p.name,
            label:        p.label || p.name,
            status:       'running',
            spec:         null,
            row_count:    null,
            error:        null,
          },
        ],
      }));
      return { ...state, messages };
    }

    case STREAM_TOOL_END: {
      const p = action.payload || {};
      const trace = {
        tool:      p.name,
        spec:      p.spec || {},
        row_count: p.row_count,
        ok:        p.ok,
        error:     p.error,
      };
      // Synthetic file-import receipts stay out of the "queries
      // executed" audit trail (the persisted turn excludes them too);
      // their pill in `blocks` is the receipt.
      const isImport = p.name === 'import_file';
      const messages = updateLastAssistant(state.messages, (m) => ({
        ...m,
        sources: isImport ? (m.sources || []) : [...(m.sources || []), trace],
        blocks:  updateToolBlock(m.blocks || [], p.tool_call_id, {
          status:    p.ok ? 'success' : 'error',
          spec:      p.spec || {},
          row_count: p.row_count,
          error:     p.error,
        }),
      }));
      return { ...state, messages };
    }

    case STREAM_ATTACHMENT: {
      const p = action.payload || {};
      const messages = updateLastAssistant(state.messages, (m) => ({
        ...m,
        blocks: [
          ...(m.blocks || []),
          { type: 'attachment', kind: p.kind, spec: p.spec },
        ],
      }));
      // A goal reaching "serving" through a chat tool call is the
      // handoff moment - the dock watches this to walk the user to
      // the goal page.
      const isMilestone = p.kind === 'artifact' && p.spec?.milestone;
      // show_page tour stop: the dock watches this to navigate while
      // the chat docks alongside.
      const isTour = p.kind === 'page_tour' && p.spec?.path;
      return {
        ...state,
        messages,
        lastMilestone: isMilestone
          ? { spec: p.spec, ts: Date.now() }
          : state.lastMilestone,
        lastPageTour: isTour
          ? { spec: p.spec, ts: Date.now() }
          : state.lastPageTour,
      };
    }

    case 'SIGNAL_ASK_DATA_STREAM_WARNING': {
      const p = action.payload || {};
      const messages = updateLastAssistant(state.messages, (m) => ({
        ...m,
        blocks: [
          ...(m.blocks || []),
          { type: 'warning', kind: p.kind, message: p.message },
        ],
      }));
      return { ...state, messages };
    }

    case STREAM_DONE: {
      const p = action.payload || {};
      const usage = p.usage || null;
      const finalEnvelope = p.envelope || null;
      const messages = updateLastAssistant(state.messages, (m) => ({
        ...m,
        pending:    false,
        streaming:  false,
        // Server buffer is authoritative for `content` (used by
        // older code paths) but the canonical render path is
        // `blocks` from the live state - we keep them.
        content:    finalEnvelope?.text ?? m.content ?? '',
        envelope:   finalEnvelope || m.envelope,
        sources:    p.sources || m.sources || [],
        // Prefer the server's authoritative blocks if they came
        // through, else keep the live blocks built during the
        // stream (these should match).
        blocks:     (Array.isArray(p.blocks) && p.blocks.length)
          ? p.blocks
          : (m.blocks || []),
        usage,
        created_at: new Date().toISOString(),
      }));
      // Update session metadata so the sidebar shows the new turn.
      let sessions = state.sessions;
      if (p.session_id) {
        const cost = usage?.cost_credits || 0;
        const found = sessions.find((s) => s.session_id === p.session_id);
        if (found) {
          sessions = sessions.map((s) =>
            s.session_id === p.session_id
              ? {
                  ...s,
                  title:      p.title || s.title,
                  updated_at: new Date().toISOString(),
                  turn_count: (s.turn_count || 0) + 2,
                  total_credits_spent: (s.total_credits_spent || 0) + cost,
                }
              : s,
          );
        } else {
          sessions = [
            {
              session_id: p.session_id,
              title:      p.title || 'New conversation',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              turn_count: 2,
              total_credits_spent: cost,
            },
            ...sessions,
          ];
        }
      }
      return {
        ...state,
        isLoading:       false,
        activeSessionId: p.session_id || state.activeSessionId,
        messages,
        sessions,
        lastUsage:       usage,
        totalCredits:    state.totalCredits + (usage?.cost_credits || 0),
        error:           null,
      };
    }

    case STREAM_ERROR: {
      const p = action.payload || {};
      // Drop the in-flight assistant placeholder. Surface a friendly
      // error so the UI can render an alert / CTA.
      const messages = state.messages.length && state.messages[state.messages.length - 1]?.streaming
        ? state.messages.slice(0, -1)
        : state.messages;
      return {
        ...state,
        isLoading: false,
        messages,
        error: {
          kind:    p.kind || 'error',
          message: p.message || 'Stream failed',
          balance: p.balance,
        },
      };
    }

    default:
      return state;
  }
};


// Helper: replace the last assistant message in-place. Used by all
// streaming reducers since each delta updates the same bubble.
function updateLastAssistant(messages, transform) {
  if (!messages.length) return messages;
  const lastIdx = messages.length - 1;
  const last = messages[lastIdx];
  if (!last || last.role !== 'assistant') return messages;
  const next = transform(last);
  if (next === last) return messages;
  const copy = messages.slice();
  copy[lastIdx] = next;
  return copy;
}


// Append streamed text to the trailing text block, or open a new
// text block if the last block is a tool/attachment. This preserves
// the chronology when text is interleaved with tool calls (e.g.
// "Let me fetch this" -> tool -> "Here's what I found" -> attachment).
function appendTextDelta(blocks, text) {
  if (!text) return blocks;
  const last = blocks[blocks.length - 1];
  if (last && last.type === 'text') {
    const updated = { ...last, text: (last.text || '') + text };
    return [...blocks.slice(0, -1), updated];
  }
  return [...blocks, { type: 'text', text }];
}


// Patch a specific tool block (matched by tool_call_id) without
// touching the order of other blocks.
function updateToolBlock(blocks, toolCallId, patch) {
  if (!toolCallId) return blocks;
  let changed = false;
  const next = blocks.map((b) => {
    if (b.type !== 'tool' || b.tool_call_id !== toolCallId) return b;
    changed = true;
    return { ...b, ...patch };
  });
  return changed ? next : blocks;
}
